import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import Icon from "Components/Icons/Icon";
import ManageSectionModal from "Components/Quotes/ManageQuotes/ManageSectionModal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { selectSectionsState } from "slices/section/reducer";
import { deleteSection, getSections } from "slices/thunks";
import { SectionPayloadData } from "types/section.api";
import parse from "html-react-parser";
import "../../../assets/scss/pages/_renderHtml.scss";

import { Card, CardBody } from "reactstrap";
import DeleteModal from "Components/Common/DeleteModal";

const SectionsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showManageSectionModal, setShowManageSectionModal] = useState(false);
  const [isEditSectionModal, setIsEditSectionModal] = useState<boolean>(false);
  const [selectedSection, setSelectedSection] =
    useState<SectionPayloadData | null>(null);

  function toggleManageSectionModal() {
    setIsEditSectionModal(false);
    setSelectedSection(null);
    setShowManageSectionModal(!showManageSectionModal);
  }

  const dispatch: any = useDispatch();

  const sectionSelector = useMemo(
    () =>
      createSelector(selectSectionsState, (state) => ({
        sections: state.sections,
        isLoading: state.status.fetch,
        meta: state.meta,
      })),
    []
  );

  const { sections, meta, isLoading } = useSelector(sectionSelector);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  const handleEditSection = useCallback((section: SectionPayloadData) => {
    setSelectedSection(section);
    setIsEditSectionModal(true);
    setShowManageSectionModal(true);
  }, []);

  const handleDeleteSection = useCallback((section: SectionPayloadData) => {
    setSelectedSection(section);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteSelectedSection = () => {
    if (selectedSection) {
      dispatch(
        deleteSection({
          sectionId: selectedSection?.id,
        })
      );
      setShowDeleteModal(false);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "ORDER",
        accessorKey: "sort",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "SECTION",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "DESCRIPTION",
        accessorKey: "content",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          // <span
          //   className="fw-normal text-black2D fs-14 font-primary"
          //   dangerouslySetInnerHTML={{ __html: cell.getValue() }}
          // />

          <div className="html-content">{parse(cell.getValue()) ?? "-"}</div>
        ),
      },
      {
        header: "ACTIONS",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="d-flex gap-3">
            <div
              className="cursor-pointer"
              onClick={() => handleEditSection(cell.row.original)}
            >
              <Icon name="Edit" />
            </div>
            <div
              className="cursor-pointer"
              onClick={() => handleDeleteSection(cell.row.original)}
            >
              <Icon name="Delete" />
            </div>
          </div>
        ),
      },
    ];

    return baseColumns;
  }, [handleEditSection, handleDeleteSection]);

  useEffect(() => {
    dispatch(getSections({ page: currentPage, search: searchTerm }));
  }, [dispatch, currentPage, searchTerm]);

  return (
    <Card id="quoteList" className="br-10 bg-white">
      <CardBody className="pt-0">
        <TableContainer
          columns={columns}
          data={sections}
          customPageSize={sections.length}
          isGlobalFilter={true}
          divClass="table-responsive table-card mb-2 bg-white"
          tableClass="align-middle table-wrap table-borderless"
          thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
          theadClass="table-light"
          SearchPlaceholder="Search section"
          searchAlign="left"
          rightContent={
            <CustomButton
              text="New Section"
              leftIcon={<i className="ri-add-line me-1 align-bottom fs-5" />}
              className="bg-primary br-7"
              onClick={() => {
                setShowManageSectionModal(true);
              }}
            />
          }
          showPagination={false}
          totalItems={meta?.total}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          isInitialLoading={isLoading === "loading" && !sections.length}
        />
      </CardBody>
      <ManageSectionModal
        isEdit={isEditSectionModal}
        showModal={showManageSectionModal}
        toggle={toggleManageSectionModal}
        existingSectionData={selectedSection}
      />
      <DeleteModal
        show={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={handleDeleteSelectedSection}
      />
    </Card>
  );
};

export default React.memo(SectionsTable);
